import {
    AgentDTO,
    AgentResponse,
    CallRecordingIntegration,
    CMEResponse,
    CMEResponseInternal,
    DeviceResponse,
    DevicesResponse,
    PartitionAdminDTO,
    PartitionAdminResponse,
    PartitionCreationResponse,
    TelSysAdminDTO,
    TelSysCreationResponse,
    TelSysFirstSyncStatusResponse
} from '../../Types/Types'
import {
    agentAdminURL,
    agentsAdminURL,
    agentsURL,
    cmeDetailsByIDInternalURL,
    cmeDetailsByIDURL,
    currentUserSettings,
    devicesURL,
    deviceURL,
    getByUsernameURL,
    getCallRecordingIntegrationURL,
    getClientConfigurationURL,
    getCRMConfigurationURL,
    getCRMTemplate,
    getCRMTypesURL,
    getQueueReferences,
    groupMembershipURL,
    partitionBaseURL,
    partitionCreatorURL,
    partitionsBaseURL,
    partitionUpdateURL,
    telSysCreatorURL,
    telSysFirstSyncURL,
    telSysInternalURL,
    telSysUpdateURL,
    USER_DARKMODE_URL,
    USER_FAVOURITES_URL,
    usersURLByTelSysPartition,
    userURL
} from '../../constants/ApiConstants'
import { RESTMethods } from '../../constants/REST'
import { IsVarEmpty } from '../../utils/GlobalFunctions'
import { accountForDifferentReportingAccessLevelNames } from '../Mappers/UserMappers'
import { VoiceAppLinkageInfo } from '../../../React/ManageQueues/Types/Types'
import {
    AdminUserBody,
    APIData,
    APIListData,
    ApplicationResponseNotification,
    ApplicationUserResponse,
    CRMTypesResponse,
    JSONResponse,
    User
} from '../Types/Types'
import { requestGetJSON } from './REST/RESTAPIClient'
import { requestJSON } from './REST/RESTClient'
import { SidebarMenuItems } from '../../constants/ReactRouter/Header/Constants'
import { ClientConfigurationBackend } from '../../../ReactRouting/Components/Organisms/ClientConfiguration/Modal/Types/Types'
import { ClientConfigurationEntry, ClientConfigurations } from '../../Types/ClientConfigurationTypes'
import {
    CRMServerRequest,
    CRMServerResponse,
    CRMTeplateResponse
} from '../../../ReactRouting/Components/Organisms/CRMConfiguration/Modal/Types/Types'
import { CRMConfigurationEntry, CRMConfigurations } from '../../Types/CRMConfiguration/Types'
import { GroupMembershipResponse } from '../../../React/Common/Types/Types'

export const getCME = (cmeID: string, cacheTime?: number): Promise<Nullable<CMEResponse>> =>
    requestGetJSON<APIData<CMEResponse>>(cmeDetailsByIDURL(cmeID), {
        cacheTime
    }).then((res: JSONResponse<APIData<CMEResponse>>): Nullable<CMEResponse> => res?.Data)

export const getCMEInternal = (cmeID: string): Promise<Nullable<CMEResponseInternal>> =>
    requestGetJSON<CMEResponseInternal>(cmeDetailsByIDInternalURL(cmeID)).then(
        (res: JSONResponse<CMEResponseInternal>): Nullable<CMEResponseInternal> => res
    )

export const getTelSysInternal = (telSysID: string | number): Promise<JSONResponse<TelSysAdminDTO>> =>
    requestGetJSON<APIData<TelSysAdminDTO>>(
        telSysInternalURL.appendPathParams(telSysID).appendQueryParams({
            advancedFields: true,
            telSysFields: 'PartitionsRptingScopeAllEnabled'
        })
    ).then((res: JSONResponse<APIData<TelSysAdminDTO>>): JSONResponse<TelSysAdminDTO> => res?.Data)

export const getTelSys = (telSysID: string | number): Promise<JSONResponse<TelSysAdminDTO>> =>
    requestGetJSON<APIData<TelSysAdminDTO>>(telSysUpdateURL(telSysID)).then(
        (res: JSONResponse<APIData<TelSysAdminDTO>>): JSONResponse<TelSysAdminDTO> => res?.Data
    )

export const getTelSysFirstTimeSync = (
    telSysID: string | number
): Promise<JSONResponse<TelSysFirstSyncStatusResponse>> =>
    requestGetJSON<APIData<TelSysFirstSyncStatusResponse>>(telSysFirstSyncURL(telSysID)).then(
        (res: JSONResponse<APIData<TelSysFirstSyncStatusResponse>>): JSONResponse<TelSysFirstSyncStatusResponse> =>
            res?.Data
    )

export const createTelSys = (telSysDTO: TelSysAdminDTO): Promise<JSONResponse<TelSysCreationResponse>> =>
    requestJSON<APIData<TelSysCreationResponse>>(telSysCreatorURL, {
        method: RESTMethods.POST,
        body: telSysDTO
    }).then(
        (res: JSONResponse<APIData<TelSysCreationResponse>>): JSONResponse<TelSysCreationResponse> =>
            res?.Data?.TelSysID
                ? {
                      TelSysID: parseInt(res.Data.TelSysID.toString(), 10)
                  }
                : undefined
    )

export const updateTelSys = (telSysID: string | number, telSysDTO: TelSysAdminDTO): Promise<JSONResponse<void>> =>
    requestJSON<void>(telSysUpdateURL(telSysID), {
        method: RESTMethods.PUT,
        body: telSysDTO
    })

export const deleteTelSys = (telSysID: string | number): Promise<JSONResponse<void>> =>
    requestJSON<void>(telSysUpdateURL(telSysID), {
        method: RESTMethods.DELETE
    })

export const getPartition = (
    telSysID: string | number,
    partitionID: string | number
): Promise<JSONResponse<PartitionAdminDTO>> =>
    requestGetJSON<APIData<PartitionAdminDTO>>(partitionUpdateURL(telSysID, partitionID)).then(
        (res: JSONResponse<APIData<PartitionAdminDTO>>): JSONResponse<PartitionAdminDTO> => res?.Data
    )

export const getPartitionForAgents = (
    telSysID: string | number,
    partitionID: string | number,
    nonAdmin?: boolean
): Promise<JSONResponse<PartitionAdminDTO>> =>
    requestGetJSON<APIData<PartitionAdminDTO>>(
        (nonAdmin ? partitionBaseURL : partitionUpdateURL)(telSysID, partitionID)
    ).then((res: JSONResponse<APIData<PartitionAdminDTO>>): JSONResponse<PartitionAdminDTO> => res?.Data)

export const createPartition = (
    telSysID: string | number,
    partitionDTO: PartitionAdminDTO
): Promise<JSONResponse<PartitionCreationResponse>> =>
    requestJSON<APIData<PartitionCreationResponse>>(partitionCreatorURL(telSysID), {
        method: RESTMethods.POST,
        body: partitionDTO
    }).then(
        (res: JSONResponse<APIData<PartitionCreationResponse>>): JSONResponse<PartitionCreationResponse> =>
            res?.Data?.PartIDInTelSys
                ? {
                      PartIDInTelSys: parseInt(res.Data.PartIDInTelSys.toString(), 10)
                  }
                : undefined
    )

export const updatePartition = (
    telSysID: string | number,
    partitionID: string | number,
    partitionDTO: PartitionAdminDTO
): Promise<JSONResponse<PartitionAdminDTO>> =>
    requestJSON<PartitionAdminDTO>(partitionUpdateURL(telSysID, partitionID), {
        method: RESTMethods.PUT,
        body: partitionDTO
    })

export const deletePartition = (
    telSysID: string | number,
    partitionID: string | number
): Promise<JSONResponse<PartitionAdminResponse>> =>
    requestJSON<PartitionAdminResponse>(partitionUpdateURL(telSysID, partitionID), {
        method: RESTMethods.DELETE
    })

export const deleteTelSysAndPartitions = (
    telSysID?: Nullable<string | number>,
    partitionID?: Nullable<string | number>
): void => {
    if (!IsVarEmpty(telSysID) && !IsVarEmpty(partitionID)) {
        requestGetJSON<APIData<TelSysAdminDTO>>(telSysUpdateURL(telSysID)).then(
            (telSys: JSONResponse<APIData<TelSysAdminDTO>>): void => {
                if (!IsVarEmpty(telSys) && telSys?.Data) {
                    updateTelSys(telSysID, {
                        ...telSys.Data,
                        CommsEnabled: false
                    }).then((): void => {
                        requestGetJSON<APIData<PartitionAdminDTO>>(partitionUpdateURL(telSysID, partitionID)).then(
                            (partition: JSONResponse<APIData<PartitionAdminDTO>>): void => {
                                if (!IsVarEmpty(partition) && partition?.Data) {
                                    updatePartition(telSysID, partitionID, {
                                        ...partition.Data,
                                        CommsEnabled: false
                                    }).then((): void => {
                                        deleteTelSys(telSysID)
                                    })
                                }
                            }
                        )
                    })
                }
            }
        )
    }
}

export const getPartitions = (telSysID: string | number): Promise<PartitionAdminResponse[]> =>
    requestGetJSON<APIListData<PartitionAdminResponse>>(partitionsBaseURL(telSysID))
        .then(
            (partitions: JSONResponse<APIListData<PartitionAdminResponse>>): PartitionAdminResponse[] =>
                partitions?.Data?.Items ?? []
        )
        .catch((): PartitionAdminResponse[] => [])

export const getDevices = (
    telSysID: string | number,
    partitionID: string | number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    searchQuery?: string,
    cacheTime?: number,
    deviceFields?: string[],
    sendFiltersWithComma?: boolean
): Promise<DevicesResponse[]> =>
    requestGetJSON<APIListData<DevicesResponse>>(
        devicesURL(
            telSysID,
            partitionID,
            monitoredOnly,
            deviceTypeFilter,
            deviceFields,
            searchQuery,
            sendFiltersWithComma
        ),
        {
            cacheTime
        }
    )
        .then((devices: JSONResponse<APIListData<DevicesResponse>>): DevicesResponse[] => devices?.Data?.Items ?? [])
        .catch((): DevicesResponse[] => [])

export const getGroupMembers = (
    telSysID: string | number,
    partitionID: string | number,
    groupDeviceNumber: string,
    deviceTypeFilter?: string[],
    searchQuery?: string,
    deviceFields?: string,
    cacheTime?: number
): Promise<Nullable<GroupMembershipResponse>> =>
    requestGetJSON<APIData<GroupMembershipResponse>>(
        groupMembershipURL(telSysID, partitionID, groupDeviceNumber, deviceTypeFilter, searchQuery, deviceFields),
        { cacheTime }
    ).then(
        (devices: JSONResponse<APIData<GroupMembershipResponse>>): Nullable<GroupMembershipResponse> => devices?.Data
    )

export const getAgents = (
    telSysID: string | number,
    partitionID: string | number,
    monitoredOnly: boolean,
    searchQuery?: string,
    cacheTime?: number
): Promise<AgentResponse[]> =>
    requestGetJSON<APIListData<AgentResponse>>(
        agentsURL(telSysID, partitionID, monitoredOnly, undefined, searchQuery),
        { cacheTime }
    )
        .then((agents: JSONResponse<APIListData<AgentResponse>>): AgentResponse[] => agents?.Data?.Items ?? [])
        .catch((): AgentResponse[] => [])

export const getDeviceCount = (
    telSysID: string | number,
    partitionID: string | number,
    deviceFilter?: string[]
): Promise<number> =>
    getDevices(telSysID, partitionID, false, deviceFilter)
        .then((devices: DevicesResponse[]): number => devices.length ?? 0)
        .catch((): number => 0)

/**
 * Get user by username from the user INFO API.
 *
 * @param userName - The username of the user to get.
 * @param cacheTime - The time to cache the response.
 *
 * @returns A promise that resolves to the user.
 */
export const getUser = (userName: string, cacheTime?: number): Promise<JSONResponse<ApplicationUserResponse>> =>
    requestGetJSON<ApplicationUserResponse>(getByUsernameURL(userName), { cacheTime }).then(
        accountForDifferentReportingAccessLevelNames
    )

export const getUsersByTelSysAndPartition = (
    telSysID: number,
    partitionID: number,
    userFields?: string[]
): Promise<JSONResponse<APIListData<User>>> =>
    requestGetJSON(usersURLByTelSysPartition(telSysID, partitionID, userFields))

export const updateUserSettings = (
    settings: ApplicationResponseNotification
): Promise<JSONResponse<ApplicationResponseNotification>> =>
    requestJSON(currentUserSettings, {
        method: RESTMethods.PUT,
        body: settings
    })

export const createUser = (user: AdminUserBody): Promise<JSONResponse<void>> =>
    requestJSON<void>(userURL(), {
        method: RESTMethods.POST,
        body: user
    })

export const updateUser = (userName: string, user: AdminUserBody): Promise<JSONResponse<void>> =>
    requestJSON(getByUsernameURL(userName), {
        method: RESTMethods.PUT,
        body: user
    })

export const deleteUser = (userName: string): Promise<JSONResponse<void>> =>
    requestJSON<void>(getByUsernameURL(userName), {
        method: RESTMethods.DELETE
    })

export const getCallRecordingIntegration = (
    telSysID: string | number
): Promise<JSONResponse<CallRecordingIntegration[]>> =>
    requestGetJSON<CallRecordingIntegration[]>(getCallRecordingIntegrationURL(telSysID))

export const getAgent = (telSysID: number, partitionID: number, agentNumber: string): Promise<JSONResponse<AgentDTO>> =>
    requestGetJSON<APIData<AgentDTO>>(agentAdminURL(telSysID, partitionID, agentNumber)).then(
        (agents: JSONResponse<APIData<AgentDTO>>): JSONResponse<AgentDTO> => agents?.Data
    )

export const createAgent = (telSysID: number, partitionID: number, agent: AgentDTO): Promise<JSONResponse<void>> =>
    requestJSON<void>(agentsAdminURL(telSysID, partitionID), {
        method: RESTMethods.POST,
        body: agent
    })

export const updateAgent = (
    telSysID: number,
    partitionID: number,
    agentNumber: string,
    agent: AgentDTO
): Promise<JSONResponse<void>> =>
    requestJSON<void>(agentAdminURL(telSysID, partitionID, agentNumber), {
        method: RESTMethods.PUT,
        body: agent
    })

export const getDevice = (
    telSysID: string | number,
    partitionID: string | number,
    deviceNumber: string
): Promise<JSONResponse<APIData<DeviceResponse>>> =>
    requestGetJSON<APIData<DeviceResponse>>(deviceURL(`${telSysID}`, `${partitionID}`, `${deviceNumber}`))

export const getUserSettings = (): Promise<JSONResponse<ApplicationResponseNotification>> =>
    requestGetJSON<ApplicationResponseNotification>(currentUserSettings)

export const updateUserFavourites = (favourites: SidebarMenuItems[]): Promise<JSONResponse<void>> =>
    requestJSON(USER_FAVOURITES_URL, {
        method: RESTMethods.POST,
        body: { MenuItemNames: favourites }
    })

export const updateUserDarkmode = (darkMode: boolean): Promise<JSONResponse<void>> =>
    requestJSON(USER_DARKMODE_URL, {
        method: RESTMethods.PUT,
        body: { Activate: darkMode }
    })

export const getDeviceReferences = (
    telsys: string | number,
    partition: string | number,
    idOnTelSysOwner: string
): Promise<JSONResponse<VoiceAppLinkageInfo[]>> =>
    requestGetJSON(getQueueReferences(telsys, partition, idOnTelSysOwner))

/**
 * Add a new client configuration.
 *
 * @param telSysID - {@type number} - Telephony system ID.
 * @param partitionID - {@type number} - Partition ID.
 * @param deviceID - {@type string} - Device ID.
 * @param body - {@type ClientConfigurationBackend} - Client configuration data to add.
 *
 * @return Promise<Nullable<void>> - Resolves to void if successful, or null if not.
 */
export const addClientConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    body: ClientConfigurationBackend
): Promise<Nullable<void>> =>
    requestJSON(getClientConfigurationURL(telSysID, partitionID, deviceID), {
        method: RESTMethods.POST,
        body
    })

/**
 * Change an existing client configuration.
 *
 * @param telSysID - {@type number} - Telephony system ID.
 * @param partitionID - {@type number} - Partition ID.
 * @param deviceID - {@type string} - Device ID.
 * @param id - {@type string} - Client configuration ID.
 * @param body - {@type ClientConfigurationBackend} - Updated client configuration data.
 *
 * @return Promise<Nullable<void>> - Resolves to void if successful, or null if not.
 */
export const changeClientConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string,
    body: ClientConfigurationBackend
): Promise<Nullable<void>> =>
    requestJSON(getClientConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.PUT,
        body
    })

export const deleteClientConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string
): Promise<Nullable<void>> =>
    requestJSON(getClientConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.DELETE
    })

/**
 * Delete a client configuration by its ID.
 *
 * @param configs - client configurations to delete.
 *
 * @return Promise<Nullable<void>> - Resolves to void if successful, or null if not.
 */
export const deleteClientConfigurations = (configs: ClientConfigurations): Promise<Nullable<void>[]> =>
    Promise.all(
        configs.map(
            (config: ClientConfigurationEntry): Promise<Nullable<void>> =>
                deleteClientConfiguration(config.TelSysID, config.PartitionID, config.DeviceNumber, config.ID)
        )
    )

export const getCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string
): Promise<JSONResponse<APIData<CRMServerResponse>>> =>
    requestGetJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID, id))

export const addCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    body: CRMServerRequest
): Promise<Nullable<void>> =>
    requestJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID), {
        method: RESTMethods.POST,
        body
    })

export const changeCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string,
    body: CRMServerRequest
): Promise<Nullable<void>> =>
    requestJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.PUT,
        body
    })

export const deleteCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string
): Promise<Nullable<void>> =>
    requestJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.DELETE
    })

export const deleteCRMConfigurations = (configs: CRMConfigurations): Promise<Nullable<void>[]> =>
    Promise.all(
        configs.map(
            (config: CRMConfigurationEntry): Promise<Nullable<void>> =>
                deleteCRMConfiguration(
                    config.TelSysID,
                    config.PartIDInTelSys,
                    config.DeviceNumber,
                    config.CRMConfigurationID
                )
        )
    )

export const getCRMTypes = (cmeID?: string, telSysID?: string): Promise<JSONResponse<CRMTypesResponse[]>> =>
    requestGetJSON<CRMTypesResponse[]>(getCRMTypesURL(cmeID, telSysID))

export const getCRMConfigTemplate = (type: string): Promise<JSONResponse<CRMTeplateResponse>> =>
    requestGetJSON<JSONResponse<CRMTeplateResponse>>(getCRMTemplate(type))
